import type React from "react";
import { useMemo } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useNodes,
  type EdgeProps,
} from "@xyflow/react";
import { applyTensionScope } from "@/lib/fieldDebateScope";
import { computeDetourPath } from "@/lib/edgeDetour";

const TENSION_COLORS: Record<string, string> = {
  contradicts: "#f9e2af",
  qualifies: "#94e2d5",
  competes: "#fab387",
};

const TENSION_DASH: Record<string, string> = {
  contradicts: "6 4",
  qualifies: "4 4",
  competes: "4 4",
};

export function TensionEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}: EdgeProps) {
  const tensionType = (data?.tensionType as string) ?? "competes";
  const description = (data?.description as string) ?? "";
  const color = TENSION_COLORS[tensionType] ?? "#a6adc8";
  const dash = TENSION_DASH[tensionType] ?? "4 4";

  // Dagre ranks camps in a column, so a tension between non-adjacent camps
  // runs straight through the camps in between — line AND label under the
  // box. When that happens, bow the curve around the obstruction; otherwise
  // keep the stock bezier.
  const nodes = useNodes();
  const detour = useMemo(() => {
    const obstacles = nodes
      .filter((n) => n.id !== source && n.id !== target)
      .map((n) => ({
        x: n.position.x,
        y: n.position.y,
        width: n.measured?.width ?? 300,
        height: n.measured?.height ?? 190,
      }));
    return computeDetourPath(
      { x: sourceX, y: sourceY },
      { x: targetX, y: targetY },
      obstacles,
    );
  }, [nodes, source, target, sourceX, sourceY, targetX, targetY]);

  const [defaultPath, defaultLabelX, defaultLabelY] = getBezierPath({
    sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
  });
  const edgePath = detour?.path ?? defaultPath;
  const labelX = detour?.labelX ?? defaultLabelX;
  const labelY = detour?.labelY ?? defaultLabelY;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const readOnly = (data?.readOnly as boolean) ?? false;
    if (readOnly) return;
    const conceptId = (data?.conceptId as string) ?? "";
    const campAId = (data?.campAId as string) ?? "";
    const campBId = (data?.campBId as string) ?? "";
    if (!conceptId || !campAId || !campBId) return;
    applyTensionScope(
      {
        id: campAId,
        label: (data?.campALabel as string) ?? campAId,
        paper_ids: (data?.campAPaperIds as string[]) ?? [],
      },
      {
        id: campBId,
        label: (data?.campBLabel as string) ?? campBId,
        paper_ids: (data?.campBPaperIds as string[]) ?? [],
      },
      conceptId,
    );
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{ stroke: color, strokeWidth: 2, strokeDasharray: dash }}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        <button
          type="button"
          onClick={handleClick}
          className="nodrag nopan pointer-events-auto hover:underline"
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 10,
            fontStyle: "italic",
            color,
            background: "hsl(var(--background))",
            padding: "1px 6px",
            borderRadius: 4,
            border: "none",
            cursor: "pointer",
          }}
          title={description}
        >
          {tensionType}
        </button>
      </EdgeLabelRenderer>
    </>
  );
}
